import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Select, Tag, Tooltip, Space, Badge } from 'antd';
import {
  ApiOutlined, CloudServerOutlined, DatabaseOutlined, GlobalOutlined,
  SafetyCertificateOutlined, CheckCircleOutlined, WarningOutlined,
  CloseCircleOutlined, EditOutlined, SaveOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useListProjectsQuery, useListServersQuery, useEditProjectMutation, useGetDnsStatusQuery } from '../store/api';

/* ─── Role definitions ──────────────────────── */
const SERVER_ROLES = [
  { key: 'servidor_api_id',            label: 'API',             icon: <ApiOutlined />,             color: '#845adf' },
  { key: 'servidor_web_id',            label: 'Web',             icon: <CloudServerOutlined />,     color: '#06b6d4' },
  { key: 'servidor_api_failover_id',   label: 'API Failover',    icon: <SafetyCertificateOutlined />, color: '#f59e0b' },
  { key: 'servidor_web_failover_id',   label: 'Web Failover',    icon: <SafetyCertificateOutlined />, color: '#f97316' },
  { key: 'servidor_bd_id',             label: 'BD',              icon: <DatabaseOutlined />,        color: '#10b981' },
  { key: 'servidor_bd_consulta_id',    label: 'BD Consulta',     icon: <DatabaseOutlined />,        color: '#14b8a6' },
  { key: 'servidor_bd_failover_id',    label: 'BD Failover',     icon: <DatabaseOutlined />,        color: '#8b5cf6' },
  { key: 'servidor_dns_id',            label: 'DNS',             icon: <GlobalOutlined />,          color: '#ec4899' },
];

/* ─── Server status helper ──────────────────── */
function getServerStatus(server) {
  if (!server) return 'none';
  const lastUpdate = server.fechaupdatedsincro ? dayjs(server.fechaupdatedsincro) : null;
  if (!lastUpdate) return 'offline';
  const diffMin = dayjs().diff(lastUpdate, 'minute');
  if (diffMin <= 5) return 'online';
  if (diffMin <= 15) return 'warning';
  return 'offline';
}

function getStatusColor(status) {
  switch (status) {
    case 'online': return '#10b981';
    case 'warning': return '#f59e0b';
    case 'offline': return '#f43f5e';
    default: return '#cbd5e1';
  }
}

function getProjectHealth(project, serversMap) {
  const statuses = SERVER_ROLES.map(role => {
    const serverId = project[role.key];
    if (!serverId) return null;
    const server = serversMap[serverId];
    return getServerStatus(server);
  }).filter(Boolean);

  if (statuses.length === 0) return 'none';
  if (statuses.some(s => s === 'offline')) return 'offline';
  if (statuses.some(s => s === 'warning')) return 'warning';
  return 'online';
}

const healthLabels = {
  online: 'Operativo',
  warning: 'Degradado',
  offline: 'Caído',
  none: 'Sin configurar',
};

/* ─── Server Cell Component ─────────────────── */
const ServerCell = ({ serverId, serverName, serverIp, status, role, onEdit }) => {
  if (!serverId) {
    return (
      <Tooltip title={`Asignar ${role.label}`}>
        <div
          onClick={onEdit}
          style={{
            width: '100%', minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6, border: '1px dashed #d1d5db', cursor: 'pointer', color: '#94a3b8', fontSize: 11,
            transition: 'all 0.2s',
          }}
          onMouseOver={e => { e.currentTarget.style.borderColor = role.color; e.currentTarget.style.color = role.color; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#94a3b8'; }}
        >
          +
        </div>
      </Tooltip>
    );
  }

  const statusColor = getStatusColor(status);

  return (
    <Tooltip title={
      <div>
        <div style={{ fontWeight: 600 }}>{serverName}</div>
        <div style={{ fontSize: 11, opacity: 0.8 }}>{serverIp}</div>
        <div style={{ marginTop: 4 }}>
          Estado: <span style={{ color: statusColor }}>{status === 'online' ? 'Online' : status === 'warning' ? 'Degradado' : 'Offline'}</span>
        </div>
      </div>
    }>
      <div
        onClick={onEdit}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px',
          borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 500,
          background: `${statusColor}10`, border: `1px solid ${statusColor}30`,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          transition: 'all 0.2s', maxWidth: 120,
        }}
      >
        <span style={{
          width: 7, height: 7, borderRadius: '50%', background: statusColor,
          flexShrink: 0, boxShadow: `0 0 4px ${statusColor}60`,
        }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {serverName || serverIp}
        </span>
      </div>
    </Tooltip>
  );
};

/* ─── DNS Target Cell Component ─────────────── */
const DnsTargetCell = ({ dnsInfo }) => {
  if (!dnsInfo) return <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>;

  const { target, ip, domain, proxied } = dnsInfo;
  const isPrincipal = target === 'principal';
  const isFailover = target === 'failover';
  const color = isPrincipal ? '#10b981' : isFailover ? '#f59e0b' : '#94a3b8';
  const label = isPrincipal ? 'Principal' : isFailover ? 'Failover' : target === 'bd' ? 'BD' : ip;
  const bgColor = isPrincipal ? '#ecfdf5' : isFailover ? '#fffbeb' : '#f8fafc';
  const borderColor = isPrincipal ? '#a7f3d0' : isFailover ? '#fde68a' : '#e2e8f0';

  return (
    <Tooltip title={
      <div>
        <div style={{ fontWeight: 600 }}>{domain}</div>
        <div style={{ fontSize: 11 }}>IP: {ip}</div>
        <div style={{ fontSize: 11 }}>Apunta a: <span style={{ color }}>{label}</span></div>
        {proxied && <div style={{ fontSize: 11, color: '#f97316' }}>☁ Proxied (Cloudflare)</div>}
      </div>
    }>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
        borderRadius: 6, fontSize: 11, fontWeight: 600, color,
        background: bgColor, border: `1px solid ${borderColor}`,
        whiteSpace: 'nowrap',
      }}>
        {isFailover && <SwapOutlined style={{ fontSize: 10 }} />}
        {label}
        {proxied && <span style={{ fontSize: 9, color: '#f97316' }}>☁</span>}
      </div>
    </Tooltip>
  );
};

/* ─── Main Component ────────────────────────── */
const ProjectHealthPanel = () => {
  const navigate = useNavigate();
  const { data: proyectos = [] } = useListProjectsQuery(undefined, { pollingInterval: 60000 });
  const { data: servidores = [] } = useListServersQuery(undefined, { pollingInterval: 60000 });
  const { data: dnsStatus = {} } = useGetDnsStatusQuery(undefined, { pollingInterval: 300000 }); // 5 min
  const [editProject] = useEditProjectMutation();
  const [editingCell, setEditingCell] = useState(null); // { projectId, roleKey }

  const serversMap = useMemo(() => {
    const map = {};
    servidores.forEach(s => { map[s.id] = s; });
    return map;
  }, [servidores]);

  const serverOptions = useMemo(() =>
    servidores.map(s => ({
      value: s.id,
      label: `${s.nombre} (${s.ip})`,
      status: getServerStatus(s),
    })),
    [servidores]
  );

  const handleSaveRole = async (projectId, roleKey, serverId) => {
    try {
      await editProject({ id: projectId, [roleKey]: serverId || '' }).unwrap();
    } catch (e) {
      console.error('Error saving role:', e);
    }
    setEditingCell(null);
  };

  // Filter to only projects that exist (active)
  const activeProjects = useMemo(() =>
    proyectos.filter(p => p.estado !== 'Cancelado'),
    [proyectos]
  );

  if (activeProjects.length === 0) return null;

  return (
    <Card
      bordered={false}
      title={
        <Space>
          <SafetyCertificateOutlined style={{ color: '#845adf' }} />
          <span style={{ fontWeight: 600 }}>Estado de Proyectos — Infraestructura</span>
          <Badge
            count={activeProjects.length}
            style={{ backgroundColor: '#845adf', borderRadius: 10, marginLeft: 4 }}
          />
        </Space>
      }
      style={{ marginBottom: 24 }}
      bodyStyle={{ padding: 0, overflow: 'auto' }}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ ...thStyle, position: 'sticky', left: 0, background: '#f8fafc', zIndex: 2, minWidth: 160 }}>
                Proyecto
              </th>
              <th style={{ ...thStyle, minWidth: 50, textAlign: 'center' }}>Estado</th>
              <th style={{ ...thStyle, minWidth: 90, textAlign: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ color: '#6366f1', fontSize: 14 }}><SwapOutlined /></span>
                  <span style={{ fontSize: 11, fontWeight: 600 }}>DNS</span>
                </div>
              </th>
              {SERVER_ROLES.map(role => (
                <th key={role.key} style={{ ...thStyle, minWidth: 110, textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <span style={{ color: role.color, fontSize: 14 }}>{role.icon}</span>
                    <span style={{ fontSize: 11, fontWeight: 600 }}>{role.label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeProjects.map((project, idx) => {
              const health = getProjectHealth(project, serversMap);
              const healthColor = getStatusColor(health);

              return (
                <tr
                  key={project.id}
                  style={{
                    background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                    transition: 'background 0.2s',
                  }}
                  onMouseOver={e => { e.currentTarget.style.background = 'rgba(132,90,223,0.04)'; }}
                  onMouseOut={e => { e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafbfc'; }}
                >
                  <td style={{
                    ...tdStyle, position: 'sticky', left: 0, zIndex: 1,
                    background: 'inherit', fontWeight: 600,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {project.color && (
                        <span style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: project.color, flexShrink: 0,
                        }} />
                      )}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/proyecto/${project.id}`)}
                        onKeyDown={(e) => e.key === 'Enter' && navigate(`/proyecto/${project.id}`)}
                        style={{
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140,
                          cursor: 'pointer', color: '#845adf',
                        }}
                        title={project.descripcion || project.nombre}
                      >
                        {project.nombre}
                      </span>
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <Tooltip title={healthLabels[health]}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', margin: '0 auto',
                        background: `${healthColor}20`, border: `2px solid ${healthColor}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {health === 'online' && <CheckCircleOutlined style={{ fontSize: 12, color: healthColor }} />}
                        {health === 'warning' && <WarningOutlined style={{ fontSize: 12, color: healthColor }} />}
                        {health === 'offline' && <CloseCircleOutlined style={{ fontSize: 12, color: healthColor }} />}
                        {health === 'none' && <span style={{ width: 6, height: 6, borderRadius: '50%', background: healthColor }} />}
                      </div>
                    </Tooltip>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <DnsTargetCell dnsInfo={dnsStatus[project.id]} />
                  </td>
                  {SERVER_ROLES.map(role => {
                    const serverId = project[role.key];
                    const server = serverId ? serversMap[serverId] : null;
                    const status = getServerStatus(server);
                    const isEditing = editingCell?.projectId === project.id && editingCell?.roleKey === role.key;

                    // Use join-resolved names when available
                    const nameKey = role.key.replace('_id', '_nombre');
                    const ipKey = role.key.replace('_id', '_ip');
                    const serverName = project[nameKey] || server?.nombre;
                    const serverIp = project[ipKey] || server?.ip;

                    return (
                      <td key={role.key} style={{ ...tdStyle, textAlign: 'center' }}>
                        {isEditing ? (
                          <Select
                            autoFocus
                            open
                            size="small"
                            style={{ width: 160 }}
                            placeholder="Seleccionar..."
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            value={serverId || undefined}
                            options={serverOptions.map(opt => ({
                              ...opt,
                              label: (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    background: getStatusColor(opt.status),
                                  }} />
                                  {opt.label}
                                </div>
                              ),
                            }))}
                            onChange={(value) => handleSaveRole(project.id, role.key, value)}
                            onBlur={() => setEditingCell(null)}
                          />
                        ) : (
                          <ServerCell
                            serverId={serverId}
                            serverName={serverName}
                            serverIp={serverIp}
                            status={status}
                            role={role}
                            onEdit={() => setEditingCell({ projectId: project.id, roleKey: role.key })}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

/* ─── Styles ────────────────────────────────── */
const thStyle = {
  padding: '10px 8px',
  borderBottom: '2px solid #e2e8f0',
  fontSize: 12,
  fontWeight: 600,
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const tdStyle = {
  padding: '8px',
  borderBottom: '1px solid #f1f5f9',
};

export default ProjectHealthPanel;
